import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios, { AxiosError } from 'axios';

import {
    Box,
    Text,
    VStack,
    IconButton,
    Flex,
    HStack,
    Button,
    useBoolean,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';

import { ToastBoxContext } from '../../../contexts/ToastBoxContext';
import PageTitle from '../../../components/PageTitle';
import TopicForm from './TopicForm';
import ToastBox from '../../../components/ToastBox';
import apis from '../../../utils/apis';
import DeleteConfirmationDialog from '../../../components/DeleteConfirmationDialog';
import { SideBarContext } from '../../../contexts/SideBarContext';
import Placeholder from '../../../components/Placeholder';
import { DimensionObj } from '../ManageDimensions';
import SelectField from '../../../components/SelectField';
import Loading from '../../../components/Loading';
import { Question } from '../ManageQuestions';

export interface TopicObject {
    id?: string;
    name: string;
    description?: string;
    question?: Question[];
}

function ManageTopics() {
    const {
        toast,
        showToast,
        onCloseToast,
    } = useContext(ToastBoxContext);
    const {
        setSideBarNegativeTabIndex,
    } = useContext(SideBarContext);

    const [topicList, setTopicList] = useState<TopicObject[]>();
    // close toast message if open
    const [editableTopic, setEditableTopic] = useState<TopicObject>();
    const [dimensionList, setDimensionList] = useState<DimensionObj[]>();
    const [selectedDimensionId, setSelectedDimensionId] = useState<DimensionObj['id']>('');
    const [loading, setLoading] = useBoolean();

    const getSavedTopics = useCallback(
        async () => {
            try {
                if (!selectedDimensionId) {
                    setTopicList(undefined);
                    return;
                }
                setLoading.on();
                const apiUrl = `/susaf/topic/all?dimensionId=${selectedDimensionId}`;
                const response = await apis.get(apiUrl);
                const dataResponse: TopicObject[] = await response.data;

                if (!dataResponse || dataResponse.length <= 0) {
                    setTopicList(undefined);
                    setLoading.off();
                    return;
                }
                setTopicList(dataResponse);
                setLoading.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR
                console.warn({ error });
            }
        },
        [selectedDimensionId, setLoading],
    );

    const getSavedDimensions = useCallback(
        async () => {
            try {
                setLoading.on();
                const apiUrl = '/susaf/dimension/all';
                const response = await apis.get(apiUrl);
                const dataResponse: DimensionObj[] = await response.data;
                if (dataResponse.length <= 0) {
                    setLoading.off();
                    return;
                }
                setDimensionList(dataResponse);
                setLoading.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR

                console.warn({ error });
            }
        },
        [setLoading],
    );

    useEffect(() => {
        getSavedTopics();
    }, [getSavedTopics]);

    useEffect(() => {
        getSavedDimensions();
    }, [getSavedDimensions]);

    useEffect(
        () => {
            if (toast) {
                onCloseToast();
            }
        },
        [onCloseToast, toast],
    );
    const [formShown, setFormShown] = useBoolean();

    const onSaveTopic = useCallback(
        async (formData: TopicObject) => {
            try {
                if (!selectedDimensionId) {
                    return;
                }
                const requestBody = formData;
                const apiUrl = `susaf/topic/add?dimensionId=${selectedDimensionId}`;
                const response = await apis.post(apiUrl, requestBody);
                if (response.status === 200) {
                    const responseData: TopicObject = response.data;
                    setTopicList((prevTopic) => {
                        if (!prevTopic || prevTopic.length <= 0) {
                            return [responseData];
                        }
                        return [...prevTopic, responseData];
                    });

                    const successToastComponent = toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Topic added"
                                description="New topic has been added successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                    setFormShown.off();
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [onCloseToast, selectedDimensionId, setFormShown, showToast, toast],
    );

    const onCancelAction = useCallback(
        () => {
            setFormShown.off();
            if (editableTopic) {
                setEditableTopic(undefined);
            }
        },
        [setFormShown, editableTopic],
    );

    const [deletableTopic, setDeletableTopic] = useState<TopicObject>();

    const onCancelDeleteTopic = useCallback(
        () => {
            setDeletableTopic(undefined);
            setSideBarNegativeTabIndex.off();
        },
        [setSideBarNegativeTabIndex],
    );

    const onSetDeletableTopic = useCallback(
        (data: TopicObject) => {
            setDeletableTopic(data);
            setSideBarNegativeTabIndex.on();
        },
        [setSideBarNegativeTabIndex],
    );

    const onDeleteTopic = useCallback(
        async () => {
            try {
                if (!deletableTopic || !selectedDimensionId) {
                    return;
                }
                const apiUrl = `/susaf/topic/delete?topicId=${deletableTopic.id}&dimensionId=${selectedDimensionId}`;
                const deleteResponse = await apis.delete(apiUrl);
                if (!deleteResponse) {
                    return;
                }
                // TODO: update the list
                const toastComponent = toast && toast({
                    status: 'success',
                    isClosable: true,
                    variant: 'subtle',
                    id: deletableTopic.id,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Delete Success"
                            description={`Topic - ${deletableTopic.name} deleted successfully`}
                            status="success"
                        />
                    ),
                });
                showToast(toastComponent);
                setDeletableTopic(undefined);
                setTopicList((prevTopic) => {
                    if (!prevTopic) {
                        return undefined;
                    }
                    return [...prevTopic].filter(
                        (topic) => topic.id !== deletableTopic.id,
                    );
                });
            } catch (onDeleteError) {
                // TODO: HANDLE THIS ERROR
                console.warn({ onDeleteError });
            }
        },
        [deletableTopic, onCloseToast, selectedDimensionId, showToast, toast],
    );

    const onUpdateTopic = useCallback(
        async (formData: TopicObject) => {
            try {
                if (!editableTopic || !selectedDimensionId) {
                    return;
                }
                const requestBody = formData;
                const apiUrl = `susaf/topic/edit?topicId=${editableTopic.id}&dimensionId=${selectedDimensionId}`;
                const response = await apis.put(apiUrl, requestBody);
                if (response.status === 200) {
                    const updatedTopicData: TopicObject = response.data;
                    setTopicList((prevTopic) => {
                        if (!prevTopic || prevTopic.length <= 0) {
                            return [updatedTopicData];
                        }
                        const tmpTopicList = [...prevTopic];
                        const updatedTopicList = tmpTopicList.map(
                            (item) => (item.id === updatedTopicData.id
                                ? updatedTopicData : item),
                        );
                        return updatedTopicList;
                    });

                    const successToastComponent = toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Topic updated"
                                description="Topic has been updated successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                    setFormShown.off();
                    setEditableTopic(undefined);
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [editableTopic, onCloseToast, selectedDimensionId, setFormShown, showToast, toast],
    );

    const dimensionOptions = useMemo(() => {
        if (!dimensionList) {
            return [];
        }
        const mappedDimensionList = dimensionList.map((dimension) => ({
            label: dimension.name,
            value: dimension.id ?? '',
        }));

        const defaultOption = {
            label: 'Select a dimension',
            value: '',
        };

        mappedDimensionList.splice(0, 0, defaultOption);

        return mappedDimensionList;
    }, [dimensionList]);

    const onSelectDimension = useCallback(
        (value: string) => setSelectedDimensionId(value as DimensionObj['name']),
        [],
    );

    return (
        <VStack>
            <PageTitle
                title="Topics"
            />
            {!loading && !dimensionList && (
                <Box style={{ height: 200, padding: 10, display: 'flex' }}>
                    <Placeholder text="There are no dimensions available. Please add it first." />
                </Box>
            )}
            <Flex
                width="100%"
                alignItems="flex-end"
                justifyContent="space-between"
            >
                <Box width="80%">
                    <SelectField
                        options={dimensionOptions}
                        label="Select Dimension"
                        onSelectOption={onSelectDimension}
                        value={selectedDimensionId}
                    />
                </Box>
                {selectedDimensionId !== '' && (
                    <Button
                        colorScheme="brand"
                        variant="outline"
                        letterSpacing={1}
                        onClick={setFormShown.toggle}
                        width="15%"
                        height={12}
                    >
                        {formShown ? 'Close Form' : 'Add Topic'}
                    </Button>
                )}
            </Flex>
            {loading && (
                <Box width="100%">
                    <Loading message="Loading data" />
                </Box>
            )}
            {!loading && selectedDimensionId === '' && (
                <Placeholder text="Please select a dimension to get topics" />
            )}
            {selectedDimensionId !== '' && formShown && !editableTopic && (
                <Flex width="100%">
                    <Box
                        width="100%"
                        marginTop={8}
                        background="#fff"
                    >
                        <TopicForm
                            onSaveAction={onSaveTopic}
                            onCancelAction={onCancelAction}
                            editableTopic={undefined}
                        />
                    </Box>
                </Flex>
            )}
            {!loading && selectedDimensionId !== '' && (!topicList || topicList.length <= 0) && (
                <Placeholder />
            )}
            {!loading && topicList && topicList.length > 0 && topicList.map(
                (item) => (
                    <Box
                        key={item.id}
                        borderWidth={1}
                        width="100%"
                        padding={4}
                    >
                        <HStack>
                            <Text width="100%">
                                <span
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 18,
                                        textTransform: 'capitalize',
                                        marginBottom: 16,
                                    }}
                                >
                                    {item.name}
                                </span>
                                <br />
                                <span style={{ fontSize: 16 }}>
                                    {item.description}
                                </span>
                            </Text>
                            <HStack width="10%">
                                <IconButton
                                    aria-label={`edit-topic-${item.id}`}
                                    icon={<EditIcon />}
                                    height={6}
                                    width={6}
                                    fontSize={12}
                                    onClick={() => setEditableTopic(item)}
                                />
                                <IconButton
                                    aria-label={`delete-topic-${item.id}`}
                                    icon={<DeleteIcon />}
                                    height={6}
                                    width={6}
                                    background="red.600"
                                    color="white"
                                    fontSize={12}
                                    onClick={() => onSetDeletableTopic(item)}
                                />
                            </HStack>
                        </HStack>
                        {editableTopic && editableTopic.id === item.id && (
                            <Box
                                width="100%"
                                alignItems="flex-start"
                            >
                                <TopicForm
                                    onSaveAction={onUpdateTopic}
                                    onCancelAction={onCancelAction}
                                    editableTopic={editableTopic}
                                />
                            </Box>
                        )}
                    </Box>
                ),
            )}
            <DeleteConfirmationDialog
                open={!!deletableTopic}
                onCancelDelete={onCancelDeleteTopic}
                onDelete={onDeleteTopic}
                header="Delete Topic"
                areYouSureMsg="Are you sure yo want to delete the topic?"
                dialogBody={(
                    <>
                        <br />
                        {deletableTopic?.name}
                    </>
                )}
            />
        </VStack>
    );
}

export default ManageTopics;
